import DropdownMenu from "../DropdownMenu/DropdownMenu";
import type { IStatusTag } from "../StatusTag/StatusTag";
import { StatusTag } from "../StatusTag/StatusTag";
import {
  isFinishableActivityOccurrence,
  isFinishableOnBehalfActivityOccurrence,
  isRevertableActivityOccurrence,
  useMarkActivityOccurrenceAsFinished,
} from "../MarkAsFinished/useMarkActivityOccurrenceAsFinished";
import { useMarkGroupAsFinished } from "../MarkAsFinished/useMarkGroupAsFinished";
import { useLingui } from "@lingui/react/macro";
import { useIsGroupedCategory } from "@/api/ActivityGrouping";
import { LoadingOverlay } from "@medoma/components/Loading/Loading";
import {
  isFinishableGroupOrVisit,
  isRevertableGroupOrVisit,
  type IGroupOrVisit,
} from "../MarkAsFinished/markAsFinishedUtils";
import {
  getGroupOrVisitStatus,
  visitStatusSchema,
} from "@medoma/models/activities";
import type {
  IActivityOccurrence,
  IRevertableVisitStatus,
} from "@medoma/models/activities";

type IStatusTagWithDropdown = IStatusTag &
  (
    | {
        activityOccurrence: IActivityOccurrence;
        activityOccurrenceIsPartOfFinishedVisit: boolean;
        groupOrVisit?: never;
      }
    | {
        groupOrVisit: IGroupOrVisit;
        activityOccurrence?: never;
        activityOccurrenceIsPartOfFinishedVisit?: never;
      }
  );

const Trigger = ({
  showLoading,
  status,
  variant,
  size,
  timestamp,
}: { showLoading: boolean } & IStatusTag) => (
  <LoadingOverlay show={showLoading}>
    <StatusTag
      status={status}
      variant={variant}
      size={size}
      timestamp={timestamp}
    />
  </LoadingOverlay>
);

export const StatusTagWithDropdown = ({
  status,
  variant = "icon",
  size = "small",
  timestamp,
  activityOccurrence,
  activityOccurrenceIsPartOfFinishedVisit,
  groupOrVisit,
}: IStatusTagWithDropdown) => {
  const { t } = useLingui();
  const {
    markActivityOccurrenceAsFinished,
    isMarkingActivityOccurrenceAsFinished,
    revertMarkActivityOccurrenceAsFinished,
    isRevertingMarkActivityOccurrenceAsFinished,
    markActivityOccurrenceAsFinishedOnBehalf,
    isMarkingActivityOccurrenceAsFinishedOnBehalf,
  } = useMarkActivityOccurrenceAsFinished();
  const {
    markGroupAsFinished,
    isMarkingGroupAsFinished,
    revertGroupToPlanned,
    isRevertingGroupToPlanned,
  } = useMarkGroupAsFinished();

  const isGroupedCategory = useIsGroupedCategory();

  const triggerAriaLabel = t`Ändra status`;

  const revertLabelForStatus = (visitStatus: IRevertableVisitStatus) => {
    switch (visitStatus) {
      case visitStatusSchema.enum.finished:
        return t`Ångra utfört`;
      case visitStatusSchema.enum.ongoing:
        return t`Ångra påbörjat besök`;
      case visitStatusSchema.enum.travellingTo:
        return t`Ångra påbörjad resa`;
      default:
        throw visitStatus satisfies never;
    }
  };

  // Activity occurrence
  if (activityOccurrence) {
    // A grouped occurrence is finished and reverted through its group, so only
    // a standalone one offers that here.
    const isStandalone =
      isGroupedCategory(activityOccurrence.category) === false;

    if (
      isFinishableOnBehalfActivityOccurrence(
        activityOccurrence,
        activityOccurrenceIsPartOfFinishedVisit,
      )
    ) {
      return (
        <DropdownMenu
          trigger={{
            ariaLabel: triggerAriaLabel,
            custom: (
              <Trigger
                showLoading={isMarkingActivityOccurrenceAsFinishedOnBehalf}
                status={status}
                variant={variant}
                size={size}
                timestamp={timestamp}
              />
            ),
          }}
        >
          <DropdownMenu.Item
            action={() =>
              markActivityOccurrenceAsFinishedOnBehalf(activityOccurrence)
            }
            content={t`Markera som utfört`}
            disabled={isMarkingActivityOccurrenceAsFinishedOnBehalf}
          />
        </DropdownMenu>
      );
    }

    if (isStandalone && isFinishableActivityOccurrence(activityOccurrence)) {
      return (
        <DropdownMenu
          trigger={{
            ariaLabel: triggerAriaLabel,
            custom: (
              <Trigger
                showLoading={isMarkingActivityOccurrenceAsFinished}
                status={status}
                variant={variant}
                size={size}
                timestamp={timestamp}
              />
            ),
          }}
        >
          <DropdownMenu.Item
            action={() => markActivityOccurrenceAsFinished(activityOccurrence)}
            content={t`Markera som utfört`}
            disabled={isMarkingActivityOccurrenceAsFinished}
          />
        </DropdownMenu>
      );
    }

    if (isStandalone && isRevertableActivityOccurrence(activityOccurrence)) {
      return (
        <DropdownMenu
          trigger={{
            ariaLabel: triggerAriaLabel,
            custom: (
              <Trigger
                showLoading={isRevertingMarkActivityOccurrenceAsFinished}
                status={status}
                variant={variant}
                size={size}
                timestamp={timestamp}
              />
            ),
          }}
        >
          <DropdownMenu.Item
            action={() =>
              revertMarkActivityOccurrenceAsFinished(activityOccurrence)
            }
            content={t`Ångra utfört`}
            disabled={isRevertingMarkActivityOccurrenceAsFinished}
          />
        </DropdownMenu>
      );
    }
  }

  if (groupOrVisit) {
    const finishableGroup = isFinishableGroupOrVisit(groupOrVisit)
      ? groupOrVisit
      : null;
    const revertableGroup = isRevertableGroupOrVisit(groupOrVisit)
      ? groupOrVisit
      : null;

    if (finishableGroup || revertableGroup) {
      const isBusy = isMarkingGroupAsFinished || isRevertingGroupToPlanned;

      return (
        <DropdownMenu
          trigger={{
            ariaLabel: triggerAriaLabel,
            custom: (
              <Trigger
                showLoading={isBusy}
                status={status}
                variant={variant}
                size={size}
                timestamp={timestamp}
              />
            ),
          }}
        >
          {finishableGroup ? (
            <DropdownMenu.Item
              action={() => markGroupAsFinished(finishableGroup)}
              content={t`Markera som utfört`}
              disabled={isBusy}
            />
          ) : null}
          {revertableGroup ? (
            <DropdownMenu.Item
              action={() => revertGroupToPlanned(revertableGroup)}
              content={revertLabelForStatus(
                getGroupOrVisitStatus(
                  revertableGroup,
                ) as IRevertableVisitStatus,
              )}
              disabled={isBusy}
            />
          ) : null}
        </DropdownMenu>
      );
    }
  }

  return (
    <StatusTag
      status={status}
      variant={variant}
      size={size}
      timestamp={timestamp}
    />
  );
};
